import React, { useEffect, useMemo, useState } from 'react';
import { IDynamicFormWidget } from './types';
import { useDynamicFormDrawerStyles } from './style';
import EnhancedButton from '../button/EnhancedButton';
import { isEmpty } from '@utils/validationUtils';
import DynamicFormSection from './DynamicFormSection';
import { DynamicFormWidgetInputType, FormFieldType } from './types/inputTypes';
import { IAbstractRecord } from '@models';
import { validateDynamicFormWidget } from './utils';
import { cloneDeep } from 'lodash';
import clsx from 'clsx';

const DynamicFormWidget: React.FC<IDynamicFormWidget> = ({
  sections,
  buttonText,
  loadingFields = {},
  disableForm = false,
  submitButtonState = null,
  isSubmitButtonDisabled = false,
  noteSection,
  onSubmit,
  onChange,
  onCustomBlur,
  onCustomValidate,
  onAutocompleteSearch,
  initialMultiAutocompleteSearch,
}) => {
  const [values, setValues] = useState<IAbstractRecord>();
  const [errors, setErrors] = useState<IAbstractRecord>();
  const [touched, setTouched] = useState<IAbstractRecord>();

  const [validatingForm, setValidatingForm] = useState<boolean>();

  const { classes } = useDynamicFormDrawerStyles();

  const inputs: Record<string, DynamicFormWidgetInputType> = useMemo(() => {
    const tempInputs: Record<string, DynamicFormWidgetInputType> = {};
    sections.forEach((section) => {
      Object.values(section.inputs).forEach((input) => {
        tempInputs[input.name] = input;
      });
    });
    return tempInputs;
  }, [sections]);

  useEffect(() => {
    const nextValues = cloneDeep(values) || {};
    const nextErrors = cloneDeep(errors) || {};
    const nextTouched = cloneDeep(touched) || {};

    if (!values) {
      Object.keys(inputs).forEach((name) => {
        nextValues[name] = inputs[name].value;
        nextErrors[name] = inputs[name].error || '';
        nextTouched[name] = !!inputs[name].error;
      });
    } else {
      Object.keys(inputs).forEach((name) => {
        const newVal = inputs[name].value;
        nextValues[name] = newVal;
        nextErrors[name] = nextErrors[name] || inputs[name].error || '';
        nextTouched[name] = nextTouched[name] || !!inputs[name].error;
      });
    }

    setValues(nextValues);
    setErrors(nextErrors);
    setTouched(nextTouched);
  }, [sections]);

  const onFieldBlur = async (
    name: string,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
      input?: DynamicFormWidgetInputType;
    }
  ) => {
    const { subFieldName, index, input } = additionalProperties || {};
    const touchedTemp = Object.assign({}, touched);
    if (input?.type === FormFieldType.repeaterBox) {
      touchedTemp[name][index][subFieldName] = true;
    } else {
      touchedTemp[name] = true;
    }

    if (onCustomBlur) {
      setValidatingForm(true);
      const result = await onCustomBlur(
        name,
        values,
        errors,
        subFieldName,
        index
      );
      setValidatingForm(false);
      setValues(result.values);
      setErrors(result.errors);
    }

    setTouched(touchedTemp);
  };

  const onFieldChange = (
    name: string,
    value: any,
    sectionIndex?: number,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
      input?: DynamicFormWidgetInputType;
    }
  ) => {
    const { input } = additionalProperties || {};
    const errorsTemp = Object.assign({}, errors);
    const valuesTemp = Object.assign({}, values);
    const touchedTemp = Object.assign({}, touched);

    valuesTemp[name] = value;

    errorsTemp[name] = validateDynamicFormWidget(
      value,
      inputs[name],
      valuesTemp
    );
    if (onChange) {
      onChange(
        name,
        value,
        sectionIndex,
        valuesTemp,
        errorsTemp,
        touchedTemp,
        additionalProperties
      );

      //update touched array
      if (input?.type === FormFieldType.repeaterBox) {
        if (
          isEmpty(touchedTemp[name]) ||
          typeof touchedTemp[name] === 'boolean'
        ) {
          touchedTemp[name] = [];
        }

        values[name].forEach((item: Record<string, string>, index: number) => {
          touchedTemp[name][index] = touchedTemp[name][index] || {};
          Object.keys(input.inputs).forEach((key) => {
            touchedTemp[name][index][key] =
              touchedTemp[name][index][key] || false;
          });
        });
      }
    }
    setValues(valuesTemp);
    setErrors(errorsTemp);
    setTouched(touchedTemp);
  };

  const validate = async (): Promise<IAbstractRecord> => {
    setValidatingForm(true);
    let newErrors: IAbstractRecord = {};
    Object.values(inputs).forEach((input) => {
      newErrors[input.name] = validateDynamicFormWidget(
        values[input.name],
        input,
        values
      );
    });

    if (onCustomValidate) {
      newErrors = await onCustomValidate(values, newErrors);
    }

    setValidatingForm(false);
    return newErrors;
  };

  const handleSubmit = async () => {
    if (!disableForm && !validatingForm) {
      const validateErrors = await validate();

      const isInValid = Object.values(validateErrors).some((v) => {
        if (Array.isArray(v)) {
          return v.some((e) => {
            return Object.values(e).some((i) => !isEmpty(i));
          });
        }
        return !isEmpty(v);
      });

      if (isInValid) {
        const initialTouched: Record<string, boolean> = {};
        Object.values(inputs).forEach((input) => {
          initialTouched[input.name] = true;
        });
        setErrors(validateErrors);
        setTouched(initialTouched);
      } else {
        onSubmit(values);
      }
    }
  };

  return values ? (
    <form
      className={classes.drawerContent}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div>
        {sections.map((section, sectionIndex: number) => (
          <DynamicFormSection
            key={section.sectionTitle}
            sectionTitle={section.sectionTitle}
            inputs={section.inputs}
            sectionIndex={sectionIndex}
            onFieldChange={onFieldChange}
            onFieldBlur={onFieldBlur}
            disableForm={disableForm}
            loadingFields={loadingFields}
            onAutocompleteSearch={onAutocompleteSearch}
            initialMultiAutocompleteSearch={initialMultiAutocompleteSearch}
            values={values}
            errors={errors}
            touched={touched}
          />
        ))}
      </div>

      <div>
        {noteSection && (
          <p className={clsx(classes.note, noteSection.className)}>
            {noteSection.title && <b>{`${noteSection.title}`}</b>}
            {noteSection.text}
          </p>
        )}

        <div className={classes.submitButtonContainer}>
          <EnhancedButton
            type="submit"
            disabled={
              disableForm ||
              isSubmitButtonDisabled ||
              Object.values(errors).some((v) => {
                if (Array.isArray(v)) {
                  return v.some((e) => {
                    return Object.values(e).some((i) => !isEmpty(i));
                  });
                }
                return !isEmpty(v);
              })
            }
            isPrimary
            state={submitButtonState}
          >
            {buttonText || 'Submit'}
          </EnhancedButton>
        </div>
      </div>
    </form>
  ) : (
    <></>
  );
};

export default DynamicFormWidget;
